import React, {useEffect, useState} from 'react';
import DelipointManagementStyle from './DelipointManagement.style';
import Spinner from 'react-bootstrap/Spinner';
import t from '../../i18n/translate';
import redAPI from "../../network/redAPI";
import moment from 'moment';
import { CURRENCY } from '../../helper/constant';

const PAGE_LIMIT = 10;

// ─── transaction helpers ────────────────────────────────────────────────────

const TYPE_META = {
  earn: {label: 'Earned', color: '#9cc54b', bg: '#f3f9e8', sign: ''},
  redeem: {label: 'Redeemed', color: '#e53935', bg: '#fef2f2', sign: '-'},
  expire: {label: 'Expired', color: '#9e9e9e', bg: '#f5f5f5', sign: '-'},
  adjustment: {label: 'Adjustment', color: '#9cc54b', bg: '#f3f9e8', sign: null},
  promo_second_order: {label: 'Promo', color: '#9cc54b', bg: '#f3f9e8', sign: ''},
  promo_3_week_streak: {label: 'Streak', color: '#9cc54b', bg: '#f3f9e8', sign: ''},
};

const getMeta = (type) => TYPE_META[type] ?? {label: type, color: '#9cc54b', bg: '#f3f9e8', sign: null};

// ─── component ──────────────────────────────────────────────────────────────

const DelipointManagement = () => {
  const [delipointsData, setDelipointsData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // ── api calls ──────────────────────────────────────────────────────────

  const fetchBalance = async () => {
    try {
      const res = await redAPI.getDelipointBalance();
      const resp = await res.json();

      if (resp && resp?.success) {
        return resp.data;
      }

      return null;
    } catch (error) {
      console.error('fetchBalance error:', error);
      return null;
    }
  };

  const fetchTransactions = async (next = 0) => {
    try {
      const res = await redAPI.getDelipointTransactions({
        next,
        limit: PAGE_LIMIT,
      });

      const resp = await res.json();

      if (resp && resp.success) {
        return resp;
      }

      return null;
    } catch (error) {
      console.error('fetchTransactions error:', error);
      return null;
    }
  };

  // ── initial load ────────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      const [balance, txData] = await Promise.all([
        fetchBalance(),
        fetchTransactions(0),
      ]);
      setDelipointsData(balance);
      const rows = txData?.data ?? [];
      setTransactions(rows);
      setNextOffset(txData?.next ?? rows.length);
      setHasMore(txData?.hasMore ?? (txData?.next != null && rows.length >= PAGE_LIMIT));
      setInitialLoading(false);
    })();
  }, []);

  // ── load more ───────────────────────────────────────────────────────────

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    const txData = await fetchTransactions(nextOffset);
    const rows = txData?.data ?? [];
    if (rows.length > 0) {
      setTransactions(prev => [...prev, ...rows]);
    }
    setNextOffset(txData?.next ?? nextOffset + rows.length);
    setHasMore(txData?.hasMore ?? (txData?.next != null && rows.length >= PAGE_LIMIT));
    setLoadingMore(false);
  };

  // ── derive display values ────────────────────────────────────────────────
  // handle both camelCase (new API) and snake_case (legacy) field names

  const balance = delipointsData?.dpBalance ?? 0;
  const tier = delipointsData?.tier ?? null;
  const tierName = tier?.name ?? '';
  const multiplier = tier?.multiplier ?? 1;
  const nextTier = delipointsData?.nextTier ?? null;
  const nextTierText = delipointsData?.nextTierText ?? null;
  const totalSpend = delipointsData?.totalSpend ?? null;
  const tierOrderValue = nextTier?.tierOrderValue ?? 0;

  // ── render ───────────────────────────────────────────────────────────────

  if (initialLoading) {
    return (
        <DelipointManagementStyle>
          <div className="dp-loading">
            <Spinner animation="border" variant="success"/>
          </div>
        </DelipointManagementStyle>
    );
  }

  return (
      <DelipointManagementStyle>

        {/* ── balance hero card ─────────────────────────────────────────── */}
        <div className="dp-hero">
          <div className="dp-hero-left">
            <span className="dp-hero-label">{t('shop_checkout_payment_delipoints_title')}</span>
            <div className="dp-hero-balance">
              <span className="dp-hero-number">{Number(balance).toLocaleString()}</span>
              <span className="dp-hero-unit">{t('shop_checkout_payment_points_title')}</span>
            </div>
            {tier ? (
                <div className="dp-tier-badge">
                  {tier.image
                      ? <img src={tier.image} alt={tierName} className="dp-tier-img"/>
                      : <span className="dp-tier-icon">🏅</span>
                  }
                  <span className="dp-tier-name">{tierName}</span>
                  {multiplier > 1 && (
                      <span className="dp-tier-multiplier">×{multiplier}</span>
                  )}
                </div>
            ) : (
                <div className="dp-tier-badge dp-tier-none">
                  <span className="dp-tier-icon">🎯</span>
                  <span className="dp-tier-name">No tier yet</span>
                </div>
            )}
          </div>

          {tier && (
              <>
                <div className="dp-hero-divider"/>
                <div className="dp-hero-right">
                  {nextTier ? (
                      <>
                        <span className="dp-tier-progress-label">Progress to next tier</span>
                        <div className="dp-tier-progress-bar">
                          <div
                              className="dp-tier-progress-fill"
                              style={{width: `${Math.min(100, (totalSpend / tierOrderValue) * 100).toFixed(1)}%`}}
                          />
                        </div>
                        <span className="dp-tier-progress-text">
                          {Number(totalSpend).toLocaleString()} / {CURRENCY} {Number(tierOrderValue).toLocaleString()}
                        </span>
                      </>
                  ) : (
                      <span className="dp-tier-max-text">{nextTierText}</span>
                  )}
                </div>
              </>
          )}
      </div>

        {/* ── transaction history ───────────────────────────────────────── */}
        <div className="dp-section">
          <div className="dp-section-header">
            <div className="dp-section-dot"/>
            <h4 className="dp-section-title">{t('profile_user_delipoint_management_table_title')}</h4>
          </div>

          {transactions.length === 0 ? (
              <div className="dp-empty">
                <span className="dp-empty-icon">📋</span>
                <span>{t('profile_user_delipoint_management_empty')}</span>
              </div>
          ) : (
              <div className="dp-tx-list">
                {transactions.map((tx, i) => {
                  const meta = getMeta(tx.type);
                  const rawPoints = Number(tx.points ?? 0);
                  const points = Math.abs(rawPoints);
                  const sign = meta.sign ?? (rawPoints < 0 ? '-' : '');
                  const orderId = tx.orderId ?? tx.order_id ?? null;
                  const redeemedVal = tx.redeemedValue ?? tx.redeemed_value ?? null;
                  const createdAt = tx.createdAt ?? tx.created_at;

                  const title = orderId
                      ? `Order #${orderId}`
                      : tx.comment || meta.label;

                  return (
                      <div
                          key={i}
                          className="dp-tx-row"
                          style={{borderLeftColor: meta.color}}
                      >
                        <div className="dp-tx-details">
                          <div className="dp-tx-top">
                            <span className="dp-tx-title">{title}</span>
                            <div className="dp-tx-points" style={{color: meta.color}}>
                              {sign}{points.toLocaleString()}
                              <span className="dp-tx-pts">pts</span>
                            </div>
                          </div>
                          <div className="dp-tx-bottom">
                      <span
                          className="dp-tx-badge"
                          style={{background: meta.bg, color: meta.color}}
                      >
                        {meta.label}
                      </span>
                            {redeemedVal != null && (
                                <span className="dp-tx-redeemed">${parseFloat(redeemedVal).toFixed(2)} off</span>
                            )}
                            <span className="dp-tx-date">
                        {createdAt ? moment(createdAt).format('DD MMM YYYY, HH:mm') : '—'}
                      </span>
                          </div>
                        </div>
                      </div>
                  );
                })}
              </div>
          )}

          {hasMore && (
              <div className="dp-load-more">
                <button className="dp-load-btn" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore
                      ? <><Spinner as="span" animation="border" size="sm"/> Loading…</>
                      : t('comment_load_more_button')
                  }
                </button>
              </div>
          )}
        </div>

      </DelipointManagementStyle>
  );
};

export default DelipointManagement;
